import React from "react";
import {
  Button,
  Paper,
  Box,
  Typography,
  alpha,
  styled,
  darken,
} from "@mui/material";
import ViewAgendaIcon from "@mui/icons-material/ViewAgenda";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import QuizIcon from "@mui/icons-material/Quiz";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ChatIcon from "@mui/icons-material/Chat";
import SchoolIcon from "@mui/icons-material/School";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PsychologyIcon from "@mui/icons-material/Psychology";
import PersonIcon from "@mui/icons-material/Person"; // Add this to your imports
import SmartToyIcon from "@mui/icons-material/SmartToy";
import MicIcon from "@mui/icons-material/Mic";
import { Grading as GradingIcon } from "@mui/icons-material";

// Type styles - same as your current configuration
export const typeStyles = {
  lessons: {
    bgLight: "#FFFBEB",
    bgDark: "#78350F", // Darker amber
    border: "#FDE68A",
    borderDark: "#F59E0B",
    text: "#B45309",
    textDark: "#FCD34D", // Lighter text for dark mode
    icon: SchoolIcon,
  },
  quizzes: {
    bgLight: "#E8F5E9",
    bgDark: "#1B5E20",
    border: "#A5D6A7",
    borderDark: "#4CAF50",
    text: "#2E7D32",
    textDark: "#81C784",
    icon: PsychologyIcon,
  },
  flashcards: {
    bgLight: "#EEF2FF",
    bgDark: "#3A3F77", // Darker indigo that's visible on #283947
    border: "#C7D2FE",
    borderDark: "#4F46E5",
    text: "#4338CA",
    textDark: "#A5B4FC", // Lighter text for dark mode
    icon: ViewAgendaIcon,
  },
  podcasts: {
    bgLight: "#FEF2F2",
    bgDark: "#7F1D1D", // Darker red
    border: "#FECACA",
    borderDark: "#EF4444",
    text: "#B91C1C",
    textDark: "#FCA5A5", // Lighter text for dark mode
    icon: MicIcon,
  },

  hpTests: {
    bgLight: "#F5E6E8",
    bgDark: "#2F0A12",
    border: "#C1A5A9",
    borderDark: "#5E2028",
    text: "#5E2028",
    textDark: "#D3D3D3",
    icon: SchoolIcon,
  },

  assessments: {
    bgLight: "#F0F9FF",
    bgDark: "#075985", // Darker blue
    border: "#BAE6FD",
    borderDark: "#0EA5E9",
    text: "#0369A1",
    textDark: "#7DD3FC", // Lighter text for dark mode
    icon: AssignmentTurnedInIcon,
  },
  chats: {
    bgLight: "#F5F3FF",
    bgDark: "#5B21B6", // Darker purple
    border: "#DDD6FE",
    borderDark: "#8B5CF6",
    text: "#5B21B6",
    textDark: "#C4B5FD", // Lighter text for dark mode
    icon: ChatIcon,
  },

  assistentChats: {
    bgLight: "#F0F4FF",
    bgDark: "#1A2740",
    border: "#D0D9F0",
    borderDark: "#2A3A5F",
    text: "#3B5998",
    textDark: "#A2B2EC",
    icon: SmartToyIcon,
  },
};

// Tab button for the new design
export const TabButton = styled(Button)(({ theme, active }) => ({
  padding: theme.spacing(2.5, 2.5),
  borderBottom: "2px solid",
  borderBottomColor: active ? theme.palette.primary.main : "transparent",
  borderRadius: 0,
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  fontWeight: 500,
  fontSize: "0.9rem",
  textTransform: "none",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "transparent",
    color: active ? theme.palette.primary.main : theme.palette.text.primary,
    borderBottomColor: active
      ? theme.palette.primary.main
      : alpha(theme.palette.grey[300], 1),
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(1),
  },

  "& .MuiSvgIcon-root": {
    fontSize: "1.2rem",
  },
}));

// Card exactly like Design 4
export const ContentCard = styled(Paper)(({ theme, colorstyle }) => ({
  borderRadius: 16,
  overflow: "hidden",
  position: "relative",
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? colorstyle
        ? alpha(colorstyle.borderDark, 0.2)
        : alpha(theme.palette.grey[700], 0.5)
      : colorstyle
        ? alpha(colorstyle.border, 0.2)
        : theme.palette.grey[100]
  }`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? colorstyle
        ? alpha(colorstyle.bgDark, 0.3)
        : "#1c2a36" // Darker than #283947 for contrast
      : colorstyle
        ? alpha(colorstyle.bgLight, 0.4)
        : alpha(theme.palette.background.paper, 0.5),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  transition: "all 0.3s ease",
  // boxShadow:
  //   theme.palette.mode === "dark"
  //     ? "0 4px 12px rgba(0, 0, 0, 0.3)"
  //     : "0 4px 12px rgba(0, 0, 0, 0.05)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  "&:hover": {
    borderColor:
      theme.palette.mode === "dark"
        ? colorstyle
          ? colorstyle.borderDark
          : theme.palette.primary.dark
        : theme.palette.grey[200],
    backgroundColor:
      theme.palette.mode === "dark"
        ? colorstyle
          ? alpha(colorstyle.bgDark, 0.5)
          : "#152330" // Even darker for hover state in dark mode
        : colorstyle
          ? alpha(colorstyle.bgLight, 0.7)
          : alpha(theme.palette.background.paper, 0.8),
    transform: "scale(1.03)",

    "& .iconWrapper": {
      transform: "scale(1.1)",
      backgroundColor:
        theme.palette.mode === "dark"
          ? alpha(colorstyle?.bgDark || "#1c2a36", 0.9)
          : colorstyle?.bgLight || theme.palette.grey[100],
    },
    "& .iconWrapper-icon": {
      transform: "scale(1.1) rotate(5deg)",
    },
  },
}));

// Content wrapper inside card
export const CardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flexGrow: 1,
}));

// Type badge inside card
export const TypeBadge = styled(Box)(({ theme, colorstyle }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color:
    theme.palette.mode === "dark"
      ? colorstyle?.textDark || theme.palette.common.white
      : colorstyle?.text || theme.palette.text.primary,
  fontSize: "0.875rem",
  fontWeight: 500,
  marginBottom: theme.spacing(1.5),
  "& .iconWrapper": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(colorstyle?.bgDark || "#152330", 0.8)
        : darken(colorstyle?.bgLight, 0.01) || theme.palette.grey[100],
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.3s ease",
  },
  "& .iconWrapper-icon": {
    transition: "transform 0.3s ease",
    color:
      theme.palette.mode === "dark"
        ? colorstyle?.textDark || theme.palette.common.white
        : colorstyle?.text || theme.palette.primary.main,
  },
}));

// Info badge (cards count, questions count, etc.)
export const InfoBadge = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  padding: theme.spacing(0.5, 1.5),
  borderRadius: 50,
  backgroundColor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.grey[900], 0.7)
      : alpha(theme.palette.grey[100], 1),
  color:
    theme.palette.mode === "dark"
      ? theme.palette.grey[300]
      : theme.palette.text.secondary,
  display: "inline-block",
  fontWeight: theme.palette.mode === "dark" ? 500 : 400,
}));

export const ActionBox = styled(Paper)(({ theme, colorstyle }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  border: `1px solid ${
    theme.palette.mode === "dark"
      ? colorstyle
        ? alpha(colorstyle.borderDark, 0.2)
        : alpha(theme.palette.grey[700], 0.5)
      : theme.palette.grey[100]
  }`,
  backgroundColor:
    theme.palette.mode === "dark"
      ? colorstyle
        ? alpha(colorstyle.bgDark, 0.3)
        : "#1c2a36" // Darker than #283947 for contrast
      : colorstyle
        ? alpha(colorstyle.bgLight, 0.4)
        : alpha(theme.palette.background.paper, 0.5),
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  textAlign: "left",
  height: "100%",
  // boxShadow:
  //   theme.palette.mode === "dark"
  //     ? "0 4px 8px rgba(0, 0, 0, 0.25)"
  //     : "0 2px 4px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    transform: "translateY(-5px)",
    backgroundColor:
      theme.palette.mode === "dark"
        ? colorstyle
          ? alpha(colorstyle.bgDark, 0.5)
          : "#152330" // Even darker for hover state
        : colorstyle
          ? alpha(colorstyle.bgLight, 0.7)
          : alpha(theme.palette.background.paper, 0.8),
    borderColor:
      theme.palette.mode === "dark"
        ? colorstyle
          ? colorstyle.borderDark
          : theme.palette.primary.dark
        : theme.palette.grey[200],
  },
  "&:hover .plusIcon": {
    transform: "rotate(90deg)",
    color:
      theme.palette.mode === "dark"
        ? colorstyle?.textDark || "#fff"
        : colorstyle?.text || theme.palette.primary.main,
  },
}));

// Helper functions that can be used across components
export const getIconColor = (theme, colorstyle) => {
  return theme.palette.mode === "dark"
    ? colorstyle?.textDark || "#ffffff"
    : colorstyle?.text || theme.palette.primary.main;
};

export const getGradeColor = (grade) => {
  const gradeColors = {
    A: "#2E7D32",
    B: "#1976D2",
    C: "#FFA000",
    D: "#ED6C02",
    E: "#D32F2F",
    F: "#757575",
  };
  return gradeColors[grade] || "#757575";
};

export const getTypeName = (type) => {
  switch (type) {
    case "flashcards":
      return "Flashcards";
    case "quizzes":
      return "Quiz";
    case "podcasts":
      return "Podcasts";
    case "lessons":
      return "Lektioner";
    case "assessments":
      return "Bedömningar";
    default:
      return "Innehåll";
  }
};
